import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../api';
import i18n from '../i18n';
import { openDB, metaSet } from '../db/localDB';
import { saveSubscriptionToken, checkSubscriptionOffline } from '../services/subscriptionService';
import { fullSync } from '../services/syncService';
import { warmCache } from '../services/warmCache';

const AuthContext = createContext(null);

// Parse permissions si MySQL retourne une string JSON
const parseUser = (u) => {
  if (!u) return u;
  if (u.permissions && typeof u.permissions === 'string') {
    try { u.permissions = JSON.parse(u.permissions); } catch { u.permissions = {}; }
  }
  return u;
};

// Fusionne appSettings DB → localStorage so getPrintPrefs() reste valide partout
const syncAppSettings = (user) => {
  if (!user?.appSettings) return;
  try {
    const dbSettings = typeof user.appSettings === 'string' ? JSON.parse(user.appSettings) : user.appSettings;
    const PRINT_PREFS_KEY = 'print_prefs';
    const local = (() => { try { return JSON.parse(localStorage.getItem(PRINT_PREFS_KEY)) || {}; } catch { return {}; } })();
    localStorage.setItem(PRINT_PREFS_KEY, JSON.stringify({ ...local, ...dbSettings }));
  } catch { /* ignore */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Recharge le user — avec fallback offline si pas de connexion
  const reloadUser = () => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }

    return authAPI.me()
      .then(u => {
        const user = parseUser(u);
        setUser(user);
        if (user.language) {
          i18n.changeLanguage(user.language);
          localStorage.setItem('language', user.language);
        }
        syncAppSettings(user);
        // Sauvegarder le user localement pour le mode offline
        openDB().then(() => metaSet('currentUser', user)).catch(() => {});
        // Pré-charger les données en background si en ligne
        if (navigator.onLine) {
          setTimeout(() => warmCache().catch(() => {}), 3000);
        }
      })
      .catch(async () => {
        // Offline : essayer de charger le user depuis la DB locale
        try {
          await openDB();
          const { checkSubscriptionOffline: checkSub } = await import('../services/subscriptionService');
          const subStatus = await checkSub();
          if (subStatus.valid && subStatus.user) {
            const { metaGet } = await import('../db/localDB');
            const cachedUser = await metaGet('currentUser');
            if (cachedUser) {
              setUser(cachedUser);
              setLoading(false);
              return;
            }
          }
        } catch { /* ignore */ }
        localStorage.removeItem('token');
        setUser(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { reloadUser(); }, []);

  const login = async (email, password, parentEmail = null) => {
    const { token, subscriptionToken } = await authAPI.login({ email, password, ...(parentEmail ? { parentEmail } : {}) });
    localStorage.setItem('token', token);
    const freshUser = parseUser(await authAPI.me());
    if (freshUser.language) {
      i18n.changeLanguage(freshUser.language);
      localStorage.setItem('language', freshUser.language);
    }
    setUser(freshUser);
    syncAppSettings(freshUser);

    // Sauvegarder pour le mode offline
    try {
      await openDB();
      await metaSet('currentUser', freshUser);
      // Sauvegarder le token d'abonnement (valide 10j offline)
      if (subscriptionToken) {
        await saveSubscriptionToken(subscriptionToken);
      } else {
        // Générer un token local simple avec la date d'expiry
        const offlineToken = btoa(JSON.stringify({ header: 'offline' })) + '.' +
          btoa(JSON.stringify({
            userId: freshUser.id,
            email: freshUser.email,
            subscriptionExpiry: freshUser.subscriptionExpiry || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
            iat: Date.now(),
          })) + '.local';
        await saveSubscriptionToken(offlineToken);
      }
      // Sync initiale + warm cache en arrière-plan
      if (navigator.onLine) {
        fullSync().catch(() => {});
        // Pré-charger toutes les données dans IndexedDB pour le mode offline
        setTimeout(() => warmCache({ force: true }).catch(() => {}), 1500);
      }
    } catch { /* ignore offline errors */ }

    return freshUser;
  };

  const register = async (data) => {
    const result = await authAPI.register(data);
    // Si pending:true, le compte attend validation — pas de connexion automatique
    if (result.pending) return { pending: true };
    const { token, user } = result;
    localStorage.setItem('token', token);
    setUser(user);
    return { pending: false, user };
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const updateUser = (updatedUser) => setUser(updatedUser);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, reloadUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);